import dayjs from "dayjs"
import { useCartDispatch, useCartState } from "hooks/useCartV2"
import { useLang } from "hooks/useLang"
import { useRouter } from "next/router"
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import {
    ActionCardState,
    BookingStep,
    BookingStepName,
} from "types/bookingv2/booking"
import { IFinalEvent } from "types/event"
import { IDayType, ImprovedCartActionsEnum } from "types/improved-booking"
import { ItimeSlot } from "types/ticket"
import { detectClient } from "utils/clientDetection"
import { isActionCardEnabled } from "utils/helpers/bookingv2/enableActionCardConditions"
import { getNextStepAction } from "utils/helpers/bookingv2/navigationActions"
import {
    shouldShowAccreditationStep,
    shouldShowAddOnsStep,
    shouldShowCheckoutStep,
    shouldShowSelectDayStep,
    shouldShowSelectSeatStep,
    shouldShowSelectTicketsStep,
    shouldShowSelectTimeStep,
} from "utils/helpers/bookingv2/showStepConditions"
import { stepConfigurations } from "utils/helpers/bookingv2/stepConfigs"
import {
    getAvailableTimeslots,
    getTimeslotDateKey,
    isTimeslotDisabled,
} from "utils/helpers/bookingv2/ticket"
import {
    enqueueEvent,
    flush,
    trackAbandonedBooking,
    trackBookingStep,
} from "utils/internalAnalyticsQueue"

interface BookingV2ContextProps {
    event: IFinalEvent
    tickets: ItimeSlot[]
    currentStep: BookingStep
    hasPrevStep: boolean
    hasNextStep: boolean
    nextStep: {
        action: () => void
        step?: BookingStep
    }
    prevStep: {
        action: () => void
        step?: BookingStep
    }
    actionCard: {
        isTermsAgreementChecked: boolean
        toggleTermsAgreement: () => void
        isActionCardEnabled: boolean
    }
    selectedDateKey: string | null
    setSelectedDateKey: (key: string | null) => void
    navigateToStep: (step: BookingStepName, dateKey?: string) => void
    isBottomSheetOpen: boolean
    setIsBottomSheetOpen: (open: boolean) => void
}

const BookingV2Context = createContext<BookingV2ContextProps | undefined>(
    undefined
)

interface BookingProviderV2Props {
    children: ReactNode
    event: IFinalEvent
    tickets: ItimeSlot[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build the date-key string from a persisted cart day (start/end dates). */
function buildDateKeyFromCart(
    start: Date | string | undefined,
    end: Date | string | undefined
): string | null {
    if (!start || !end) return null
    return `${dayjs(start).format("YYYY-MM-DD")}_${dayjs(end).format("YYYY-MM-DD")}`
}

/** Compute the step to land on when there is exactly one enabled timeslot. */
function resolveAutoAdvanceStep(
    availableSteps: BookingStepName[]
): BookingStepName {
    // After auto-selecting a timeslot, SELECT_TIME will become visible in the
    // next render. Find the first available step that comes after SELECT_TIME
    // in the canonical booking flow order, so we don't depend on the stale
    // availableSteps from before the timeslot was selected.
    const stepsAfterTime = [
        BookingStepName.SELECT_SEAT,
        BookingStepName.SELECT_TICKETS,
        BookingStepName.ADD_ONS,
        BookingStepName.ACCREDITATION,
        BookingStepName.CHECKOUT,
    ]
    const target = stepsAfterTime.find((step) =>
        availableSteps.includes(step)
    )
    return target ?? availableSteps[0]
}

function resolveStepFromCartState(
    requestedStep: BookingStepName | undefined,
    availableSteps: BookingStepName[],
    cartState: ReturnType<typeof useCartState>
): BookingStepName | undefined {
    if (!requestedStep || !availableSteps.includes(requestedStep)) {
        return undefined
    }

    const fallbackBeforeTickets = availableSteps.includes(
        BookingStepName.SELECT_TIME
    )
        ? BookingStepName.SELECT_TIME
        : BookingStepName.SELECT_DAY

    if (
        requestedStep === BookingStepName.SELECT_TICKETS &&
        !cartState.selectedDays?.timeslot_id
    ) {
        return fallbackBeforeTickets
    }

    if (
        [
            BookingStepName.SELECT_SEAT,
            BookingStepName.ADD_ONS,
            BookingStepName.ACCREDITATION,
            BookingStepName.CHECKOUT,
        ].includes(requestedStep) &&
        cartState.selectedTickets.length === 0
    ) {
        return cartState.selectedDays?.timeslot_id
            ? BookingStepName.SELECT_TICKETS
            : fallbackBeforeTickets
    }

    return requestedStep
}

function resolveInitialStepFromCart(
    availableSteps: BookingStepName[],
    cartState: ReturnType<typeof useCartState>
): BookingStepName {
    const fallbackBeforeTickets = availableSteps.includes(
        BookingStepName.SELECT_TIME
    )
        ? BookingStepName.SELECT_TIME
        : BookingStepName.SELECT_DAY

    if (
        cartState.selectedTickets.length > 0 &&
        cartState.selectedDays?.timeslot_id &&
        availableSteps.includes(BookingStepName.SELECT_TICKETS)
    ) {
        return BookingStepName.SELECT_TICKETS
    }

    if (cartState.selectedDays?.timeslot_id) {
        return availableSteps.includes(BookingStepName.SELECT_TICKETS)
            ? BookingStepName.SELECT_TICKETS
            : fallbackBeforeTickets
    }

    if (cartState.selectedDays) {
        return fallbackBeforeTickets
    }

    return BookingStepName.SELECT_DAY
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const BookingProviderV2 = ({
    children,
    event,
    tickets,
}: BookingProviderV2Props) => {
    const router = useRouter()
    const cartState = useCartState()
    const cartDispatch = useCartDispatch()
    const { dynamicTranslate } = useLang()

    const [isTermsAgreementChecked, setIsTermsAgreementChecked] =
        useState<boolean>(false)
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)

    // ---------------------------------------------------------------------------
    // Analytics: Booking session tracking
    // ---------------------------------------------------------------------------
    // Generate a unique booking session ID that persists for this booking flow
    const bookingSessionIdRef = useRef<string>("")
    const bookingCompletedRef = useRef<boolean>(false)
    const autoSelectAnalyticsSentRef = useRef<boolean>(false)
    const checkoutAnalyticsSentRef = useRef<boolean>(false)
    const autoSelectedTimeslotIdRef = useRef<string | null>(null)

    // Initialize booking session ID on mount
    if (!bookingSessionIdRef.current) {
        bookingSessionIdRef.current =
            typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
    }

    const toggleTermsAgreement = () =>
        setIsTermsAgreementChecked(!isTermsAgreementChecked)

    const availableTimeslots = useMemo(
        () => getAvailableTimeslots(tickets),
        [tickets]
    )
    const selectedTickets = cartState.selectedTickets
    const enabledTimeslots = useMemo(
        () => availableTimeslots.filter((slot) => !isTimeslotDisabled(slot)),
        [availableTimeslots]
    )
    const hasSingleEnabledTimeslot = enabledTimeslots.length === 1

    // ---------------------------------------------------------------------------
    // selectedDateKey — derived on first render, then owned as state
    // ---------------------------------------------------------------------------
    // Initialise from the persisted cart so we never need an effect to sync it.
    const [selectedDateKey, setSelectedDateKey] = useState<string | null>(() =>
        buildDateKeyFromCart(
            cartState.selectedDays?.start,
            cartState.selectedDays?.end
        )
    )

    useEffect(() => {
        const nextDateKey = buildDateKeyFromCart(
            cartState.selectedDays?.start,
            cartState.selectedDays?.end
        )

        if (nextDateKey !== selectedDateKey) {
            setSelectedDateKey(nextDateKey)
        }
    }, [
        cartState.selectedDays?.start,
        cartState.selectedDays?.end,
        selectedDateKey,
    ])

    // availableSteps depends on selectedDateKey, so compute it before deriving
    // the initial step.
    const availableSteps = useMemo(
        () =>
            [
                shouldShowSelectDayStep(availableTimeslots) &&
                    BookingStepName.SELECT_DAY,
                shouldShowSelectTimeStep(selectedDateKey, availableTimeslots) &&
                    BookingStepName.SELECT_TIME,
                shouldShowSelectTicketsStep(event) &&
                    BookingStepName.SELECT_TICKETS,
                shouldShowSelectSeatStep(event, selectedTickets) &&
                    BookingStepName.SELECT_SEAT,
                shouldShowAddOnsStep(event) && BookingStepName.ADD_ONS,
                shouldShowAccreditationStep(event) &&
                    BookingStepName.ACCREDITATION,
                shouldShowCheckoutStep(event) && BookingStepName.CHECKOUT,
            ].filter(Boolean) as BookingStepName[],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedDateKey, availableTimeslots, event, selectedTickets]
    )

    // ---------------------------------------------------------------------------
    // currentStepName — derived on first render, then owned as state
    // ---------------------------------------------------------------------------
    const [currentStepName, setCurrentStepName] = useState<BookingStepName>(
        () => {
            // If a bookingStep query param was provided honour it immediately.
            const stepMap: Record<string, BookingStepName> = {
                select_day: BookingStepName.SELECT_DAY,
                select_time: BookingStepName.SELECT_TIME,
                select_tickets: BookingStepName.SELECT_TICKETS,
                select_seat: BookingStepName.SELECT_SEAT,
                accreditation: BookingStepName.ACCREDITATION,
                add_ons: BookingStepName.ADD_ONS,
                checkout: BookingStepName.CHECKOUT,
            }
            const paramStep =
                typeof router.query.bookingStep === "string"
                    ? stepMap[router.query.bookingStep.toLowerCase()]
                    : undefined
            const resolvedParamStep = resolveStepFromCartState(
                paramStep,
                availableSteps,
                cartState
            )
            if (resolvedParamStep) return resolvedParamStep

            return resolveInitialStepFromCart(availableSteps, cartState)
        }
    )

    // ---------------------------------------------------------------------------
    // Analytics: Track booking step transitions
    // ---------------------------------------------------------------------------
    useEffect(() => {
        // Skip tracking for webview clients - mobile app tracks its own events
        // The client_session_id is still set for deduplication
        const clientType = detectClient()
        if (clientType === "webview") return

        // Track the current step
        trackBookingStep(bookingSessionIdRef.current, currentStepName, {
            event_id: event._id,
            event_name: event.en_title || event.ar_title,
            has_selected_tickets: (cartState.selectedTickets?.length ?? 0) > 0,
            has_selected_date: !!cartState.selectedDays,
            total_tickets: cartState.selectedTickets?.length ?? 0,
        })

        // Also send funnel-specific events for dashboard compatibility
        // Map booking steps to funnel event names expected by the analytics dashboard
        if (
            currentStepName === BookingStepName.SELECT_DAY &&
            cartState.selectedDays
        ) {
            enqueueEvent({
                event_name: "day_selected",
                event_id: event._id,
                properties: {
                    event_title: event.en_title || event.ar_title,
                    has_selected_date: true,
                },
            })
        }

        if (
            currentStepName === BookingStepName.SELECT_TICKETS &&
            (cartState.selectedTickets?.length ?? 0) > 0
        ) {
            enqueueEvent({
                event_name: "ticket_selected",
                event_id: event._id,
                properties: {
                    event_title: event.en_title || event.ar_title,
                    total_tickets: cartState.selectedTickets?.length ?? 0,
                },
            })
        }

        if (
            currentStepName === BookingStepName.CHECKOUT &&
            !checkoutAnalyticsSentRef.current
        ) {
            checkoutAnalyticsSentRef.current = true
            enqueueEvent({
                event_name: "proceed_to_checkout",
                event_id: event._id,
                properties: {
                    event_id: event._id,
                    event_title: event.ar_title,
                    total_tickets: cartState.selectedTickets?.length ?? 0,
                },
            })
        }
    }, [
        currentStepName,
        event._id,
        event.en_title,
        event.ar_title,
        cartState.selectedTickets,
        cartState.selectedDays,
    ])

    // ---------------------------------------------------------------------------
    // Analytics: Track abandoned bookings on page leave
    // ---------------------------------------------------------------------------
    useEffect(() => {
        // Skip tracking for webview clients
        if (detectClient() === "webview") return

        const handleBeforeUnload = () => {
            // Only track abandonment if booking is in progress and not completed
            if (bookingCompletedRef.current) return

            const hasTickets = (cartState.selectedTickets?.length ?? 0) > 0
            const hasDate = !!cartState.selectedDays

            // Consider abandoned if they have made any selections
            if (hasTickets || hasDate) {
                trackAbandonedBooking(
                    bookingSessionIdRef.current,
                    currentStepName,
                    {
                        event_id: event._id,
                        event_name: event.en_title || event.ar_title,
                        has_selected_tickets: hasTickets,
                        has_selected_date: hasDate,
                        total_tickets: cartState.selectedTickets?.length ?? 0,
                    }
                )
                flush() // Ensure event is sent before page closes
            }
        }

        window.addEventListener("beforeunload", handleBeforeUnload)
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload)
    }, [
        currentStepName,
        event._id,
        event.en_title,
        event.ar_title,
        cartState.selectedTickets,
        cartState.selectedDays,
    ])

    // ---------------------------------------------------------------------------
    // Side-effect 1: keep event ID in cart (external store sync — must be effect)
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (event?._id && cartState.event !== event._id) {
            cartDispatch({
                type: ImprovedCartActionsEnum.ADD_EVENT,
                payload: { event: event._id, slug: event.slug },
            })
        }
    }, [event?._id, event?.slug, cartState.event, cartDispatch])

    // ---------------------------------------------------------------------------
    // Side-effect 2: auto-select the only enabled timeslot on first open
    // (dispatches to external cart store — must be effect)
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (hasSingleEnabledTimeslot && !cartState.selectedDays) {
            const timeslot = enabledTimeslots[0]
            if (
                !timeslot ||
                autoSelectedTimeslotIdRef.current === timeslot._id
            ) {
                return
            }
            const currentDate = dayjs(timeslot.start_slot)
            const availableTickets = Array.isArray(event.availableTickets)
                ? event.availableTickets
                : []

            const mappedTypes: IDayType[] = timeslot.types.map((type) => {
                const id =
                    typeof type.type_id === "string"
                        ? type.type_id
                        : //@ts-ignore
                          type.type_id?._id || ""

                const ticketDetails = availableTickets.find((t) => t._id === id)

                if (!ticketDetails) {
                    return {
                        _id: id,
                        type_id: id,
                        ar_description: "",
                        en_description: "",
                        en_title: "",
                        ar_title: "",
                        color: "#ffffff",
                        discount: {
                            current_value: 0,
                            previous_value: 0,
                        },
                        limit: type.limit,
                        max: 0,
                        min: 1,
                        must_select_seat: false,
                        price: 0,
                        group: undefined,
                        wait_list: type.wait_list,
                    }
                }

                return {
                    _id: id,
                    type_id: id,
                    ar_description: ticketDetails?.ar_description ?? "",
                    en_description: ticketDetails?.en_description ?? "",
                    en_title: ticketDetails?.en_title ?? "",
                    ar_title: ticketDetails?.ar_title ?? "",
                    color: ticketDetails?.color ?? "#ffffff",
                    discount: ticketDetails?.discount ?? {
                        current_value: 0,
                        previous_value: 0,
                    },
                    limit: type.limit,
                    max: ticketDetails?.max ?? 0,
                    min: ticketDetails?.min ?? 1,
                    must_select_seat: ticketDetails?.must_select_seat ?? false,
                    price: ticketDetails?.price ?? 0,
                    group: (ticketDetails as any)?.group,
                    wait_list: type.wait_list,
                }
            })

            cartDispatch({
                type: ImprovedCartActionsEnum.ADD_DAY,
                payload: {
                    timeslot_id: timeslot._id,
                    dayName: currentDate.format("dddd"),
                    dayNumber: currentDate.date(),
                    monthName: currentDate.format("MMMM"),
                    monthNum: currentDate.month() + 1,
                    yearNum: currentDate.year().toString(),
                    start: new Date(timeslot.start_slot),
                    end: new Date(timeslot.end_slot),
                    types: mappedTypes,
                    seats_season_id:
                        timeslot.seats_season_id ||
                        event.seatsio?.season_id ||
                        "",
                    allow_using_coupon: timeslot.allow_using_coupon,
                },
            })

            autoSelectedTimeslotIdRef.current = timeslot._id
            setSelectedDateKey(getTimeslotDateKey(timeslot))
            setCurrentStepName(resolveAutoAdvanceStep(availableSteps))

            // Fire a single analytics event to record the system-triggered day selection
            if (
                !autoSelectAnalyticsSentRef.current &&
                detectClient() !== "webview"
            ) {
                autoSelectAnalyticsSentRef.current = true
                enqueueEvent({
                    event_name: "day_selected",
                    event_id: event._id,
                    properties: {
                        event_id: event._id,
                        event_title: event.en_title || event.ar_title,
                        auto_selected: true,
                        source: "system",
                        day: getTimeslotDateKey(timeslot),
                    },
                })
            }
        }
        if (!hasSingleEnabledTimeslot) {
            autoSelectedTimeslotIdRef.current = null
        }
    }, [
        hasSingleEnabledTimeslot,
        cartState.selectedDays,
        cartDispatch,
        event,
        availableSteps,
    ])

    // ---------------------------------------------------------------------------
    // Side-effect 3: reset step when the cart loses its timeslot_id.
    // This happens when useBookingPageInitialization clears the cart because
    // the previously selected timeslot is no longer valid. Without this guard
    // the user lands on SELECT_TICKETS (or a later step) with no timeslot data.
    // ---------------------------------------------------------------------------
    useEffect(() => {
        const stepsRequiringTimeslot = [
            BookingStepName.SELECT_TICKETS,
            BookingStepName.SELECT_SEAT,
            BookingStepName.ADD_ONS,
            BookingStepName.ACCREDITATION,
            BookingStepName.CHECKOUT,
        ]

        if (
            stepsRequiringTimeslot.includes(currentStepName) &&
            !cartState.selectedDays?.timeslot_id
        ) {
            const fallback = availableSteps.includes(
                BookingStepName.SELECT_TIME
            )
                ? BookingStepName.SELECT_TIME
                : BookingStepName.SELECT_DAY

            if (availableSteps.includes(fallback)) {
                setCurrentStepName(fallback)
            }
        }
    }, [currentStepName, cartState.selectedDays?.timeslot_id, availableSteps])

    // ---------------------------------------------------------------------------
    // Query-param bookingStep — update step when URL changes after mount
    // ---------------------------------------------------------------------------
    useEffect(() => {
        const stepMap: Record<string, BookingStepName> = {
            select_day: BookingStepName.SELECT_DAY,
            select_time: BookingStepName.SELECT_TIME,
            select_tickets: BookingStepName.SELECT_TICKETS,
            select_seat: BookingStepName.SELECT_SEAT,
            accreditation: BookingStepName.ACCREDITATION,
            add_ons: BookingStepName.ADD_ONS,
            checkout: BookingStepName.CHECKOUT,
        }

        if (
            router.query.bookingStep &&
            typeof router.query.bookingStep === "string"
        ) {
            const param = router.query.bookingStep.toLowerCase()
            const stepName = stepMap[param]
            const resolvedStepName = resolveStepFromCartState(
                stepName,
                availableSteps,
                cartState
            )
            if (resolvedStepName) {
                setCurrentStepName(resolvedStepName)

                const { bookingStep, ...restQuery } = router.query
                void router.replace(
                    {
                        pathname: router.pathname,
                        query: restQuery,
                    },
                    undefined,
                    { shallow: true }
                )
            }
        }
    }, [
        router,
        router.query,
        router.query.bookingStep,
        availableSteps,
        cartState,
    ])

    const currentStepIndex = availableSteps.indexOf(currentStepName)
    const currentStep: BookingStep = stepConfigurations[currentStepName]
    const isCurrentStepAvailable = currentStepIndex >= 0
    const hasNextStep =
        isCurrentStepAvailable && currentStepIndex < availableSteps.length - 1

    const rawPrevIndex = currentStepIndex - 1
    const skipSeatOnBack =
        isCurrentStepAvailable &&
        rawPrevIndex >= 0 &&
        availableSteps[rawPrevIndex] === BookingStepName.SELECT_SEAT &&
        currentStepName !== BookingStepName.CHECKOUT

    const prevStepName = skipSeatOnBack
        ? availableSteps[rawPrevIndex - 1]
        : isCurrentStepAvailable
          ? availableSteps[rawPrevIndex]
          : undefined

    const hasPrevStep = !!prevStepName

    const prevStep: BookingStep | undefined = prevStepName
        ? stepConfigurations[prevStepName]
        : undefined
    const nextStep: BookingStep | undefined = hasNextStep
        ? stepConfigurations[availableSteps[currentStepIndex + 1]]
        : undefined

    const nextStepAction = () => {
        // Mark booking as completed when reaching checkout confirmation
        // This prevents tracking as abandoned when purchase is successful
        if (currentStepName === BookingStepName.CHECKOUT && !hasNextStep) {
            bookingCompletedRef.current = true
        }

        // Check for waitlist tickets requiring authentication
        const hasWaitlistTickets = cartState.selectedTickets?.some(
            (ticket) => ticket.isWaitlist === true
        )

        if (hasWaitlistTickets) {
            // This will be handled by the SelectTickets component's AuthModal
            // The SelectTickets component will intercept this and show auth modal
            // For now, proceed with normal flow - the SelectTickets component will handle the modal
        }

        // Use the current dynamic route param for event identifier so that
        // if a slug is used in the URL, we keep using that slug when
        // navigating to the checkout page. Fall back to event._id only
        // if the route param is not available.
        const eventSlugOrId =
            (router.query.eventid as string | undefined) || event?._id

        return getNextStepAction({
            availableSteps,
            currentStep,
            prevStep,
            nextStep,
            setCurrentStepName,
            router,
            eventId: eventSlugOrId,
        })
    }

    const prevStepAction = () => {
        if (prevStepName) {
            setCurrentStepName(prevStepName)
        }
    }

    const navigateToStep = (step: BookingStepName, dateKey?: string) => {
        if (dateKey) {
            setSelectedDateKey(dateKey)
            setCurrentStepName(step)
        } else {
            setCurrentStepName(step)
        }
    }

    const actionCardState: ActionCardState = {
        currentStepName,
        cartState,
        isTermsAgreementChecked,
    }

    const isEnabled = isActionCardEnabled(
        actionCardState,
        router.pathname,
        dynamicTranslate,
        event
    )

    return (
        <BookingV2Context.Provider
            value={{
                event,
                tickets: availableTimeslots,
                currentStep,
                hasPrevStep,
                hasNextStep,
                nextStep: {
                    action: nextStepAction,
                    step: nextStep,
                },
                prevStep: {
                    action: prevStepAction,
                    step: prevStep,
                },
                actionCard: {
                    isTermsAgreementChecked,
                    toggleTermsAgreement,
                    isActionCardEnabled: isEnabled,
                },
                selectedDateKey,
                setSelectedDateKey,
                navigateToStep,
                isBottomSheetOpen,
                setIsBottomSheetOpen,
            }}
        >
            {children}
        </BookingV2Context.Provider>
    )
}

export const useBookingV2 = () => {
    const context = useContext(BookingV2Context)
    if (!context) {
        throw new Error("useBooking must be used within a BookingProviderV2")
    }
    return context
}
